import React, { useState, useEffect } from 'react'
import api from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import Icon from '../../components/Icon'
import { FormGroup, Input, Button } from '../../components/ui'
import { translateApiError } from '../../utils/errorMessages'
import './Admin.css'

const REFERENCE_TYPE_NAMES = {
  клиент_статус: 'Статусы клиентов',
  клиент_город: 'Города клиентов',
  подразделение: 'Подразделения',
  тип_расходов: 'Типы расходов',
  категория_расходов: 'Категории расходов',
  типы_распределения: 'Типы распределения',
  статус_договора: 'Статусы договоров',
  квартал: 'Кварталы',
  период: 'Периоды',
}

function AdminReference() {
  const { user } = useAuth()
  const canManage = user?.permissions?.['reference.manage']
  const [references, setReferences] = useState({})
  const [customReferences, setCustomReferences] = useState([])
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)
  const [expandedKeys, setExpandedKeys] = useState({})
  const [modal, setModal] = useState(null)
  const [valueEdit, setValueEdit] = useState(null)
  const [valueDelete, setValueDelete] = useState(null)
  const [replaceWithId, setReplaceWithId] = useState('')

  const fetchReferences = async () => {
    try {
      setLoading(true)
      const response = await api.get('/admin/reference')
      setReferences(response.data.references || {})
      setCustomReferences(response.data.custom_references || [])
      const sysKeys = Object.keys(response.data.references || {})
      const customKeys = (response.data.custom_references || []).map(r => `custom_${r.id}`)
      const expanded = {}
      ;[...sysKeys, ...customKeys].forEach(k => { expanded[k] = true })
      setExpandedKeys(prev => ({ ...expanded, ...prev }))
    } catch (error) {
      console.error('Error fetching references:', error)
      alert('Ошибка загрузки справочников: ' + translateApiError(error.response?.data?.message || error.response?.data?.error || error.message))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchReferences()
  }, [])

  const toggleExpand = (key) => {
    setExpandedKeys(prev => ({ ...prev, [key]: !prev[key] }))
  }

  const handleSync = async () => {
    if (!window.confirm('Вы уверены, что хотите синхронизировать справочники из Google Sheets? Новые значения будут добавлены в базу данных.')) return
    try {
      setSyncing(true)
      const response = await api.post('/admin/reference/sync')
      const message = `Синхронизация завершена успешно!\n\nВсего добавлено значений: ${response.data.total_values}\n\nПо справочникам:\n` +
        Object.entries(response.data.reference_types || {})
          .map(([type, count]) => `  ${type}: ${count} значений`)
          .join('\n')
      alert(message)
      fetchReferences()
    } catch (error) {
      console.error('Error syncing references:', error)
      alert('Ошибка синхронизации: ' + translateApiError(error.response?.data?.message || error.response?.data?.error || error.response?.data?.details || error.message))
    } finally {
      setSyncing(false)
    }
  }

  const handleCreateReference = async (name, values) => {
    try {
      await api.post('/admin/custom-references', { name, values: values || [] })
      setModal(null)
      fetchReferences()
    } catch (err) {
      const d = err.response?.data
      alert(translateApiError(d?.message || d?.error || err.message))
    }
  }

  const handleUpdateReference = async (id, name, values) => {
    try {
      await api.put(`/admin/custom-references/${id}`, { name, values: values ?? undefined })
      setModal(null)
      fetchReferences()
    } catch (err) {
      const d = err.response?.data
      alert(translateApiError(d?.message || d?.error || err.message))
    }
  }

  const handleDeleteReference = async (id) => {
    if (!window.confirm('Удалить справочник?')) return
    try {
      await api.delete(`/admin/custom-references/${id}`)
      setModal(null)
      fetchReferences()
    } catch (err) {
      const d = err.response?.data
      alert(translateApiError(d?.message || d?.error || err.message))
    }
  }

  const handleAddSystemValue = async (refType, name, code) => {
    try {
      await api.post(`/admin/reference/system/${refType}/values`, { name, code: code || name })
      setModal(null)
      fetchReferences()
    } catch (err) {
      const d = err.response?.data
      alert(translateApiError(d?.message || d?.error || err.message))
    }
  }

  const handleAddCustomValue = async (refId, value, label) => {
    try {
      await api.post(`/admin/custom-references/${refId}/values`, { value: value || label, label: label || value })
      setModal(null)
      fetchReferences()
    } catch (err) {
      const d = err.response?.data
      alert(translateApiError(d?.message || d?.error || err.message))
    }
  }

  const handleUpdateSystemValue = async (refType, valueId, name, code, confirmUpdate) => {
    try {
      await api.put(`/admin/reference/system/${refType}/values/${valueId}`, { name, code: code || name, confirm_update: confirmUpdate })
      setValueEdit(null)
      fetchReferences()
    } catch (err) {
      const data = err.response?.data
      if (data?.error === 'value_used' && data?.usage_count && !confirmUpdate) {
        if (window.confirm(`${data.message}\n\nОбновить ссылки в сущностях?`)) {
          handleUpdateSystemValue(refType, valueId, name, code, true)
        }
      } else {
        alert(translateApiError(data?.message || data?.error || err.message))
      }
    }
  }

  const handleUpdateCustomValue = async (refId, valueId, value, label, confirmUpdate) => {
    try {
      await api.put(`/admin/custom-references/${refId}/values/${valueId}`, { value, label, confirm_update: confirmUpdate })
      setValueEdit(null)
      fetchReferences()
    } catch (err) {
      const data = err.response?.data
      if (data?.error === 'value_used' && data?.usage_count && !confirmUpdate) {
        if (window.confirm(`${data.message}\n\nОбновить ссылки в сущностях?`)) {
          handleUpdateCustomValue(refId, valueId, value, label, true)
        }
      } else {
        alert(translateApiError(data?.message || data?.error || err.message))
      }
    }
  }

  const handleDeleteValue = async (ctx, replaceWithId, confirmDataLoss) => {
    const { isSystem, refType, refId, valueId } = ctx
    try {
      if (isSystem) {
        await api.delete(`/admin/reference/system/${refType}/values/${valueId}`, { data: { replace_with_id: replaceWithId, confirm_data_loss: confirmDataLoss } })
      } else {
        await api.delete(`/admin/custom-references/${refId}/values/${valueId}`, { data: { replace_with_id: replaceWithId, confirm_data_loss: confirmDataLoss } })
      }
      setValueDelete(null)
      fetchReferences()
    } catch (err) {
      const data = err.response?.data
      if (data?.error === 'value_used' && data?.usage_count) {
        setValueDelete(prev => ({ ...prev, usageCount: data.usage_count, showReplaceModal: true }))
        setReplaceWithId('')
      } else {
        alert(translateApiError(data?.message || data?.error || err.message))
      }
    }
  }

  const confirmDeleteWithReplace = () => {
    if (replaceWithId) {
      handleDeleteValue(valueDelete, replaceWithId, false)
      setValueDelete(null)
      setReplaceWithId('')
    } else {
      alert('Выберите значение для замены')
    }
  }

  const confirmDeleteWithDataLoss = () => {
    if (window.confirm('Удаление приведёт к потере данных в сущностях. Продолжить?')) {
      handleDeleteValue(valueDelete, null, true)
      setValueDelete(null)
    }
  }

  const allRefs = [
    ...Object.entries(references).map(([type, values]) => ({
      key: type,
      name: REFERENCE_TYPE_NAMES[type] || type,
      values: values.map(v => ({ ...v, label: v.name || v.code })),
      isSystem: true,
    })),
    ...customReferences.map(r => ({
      key: `custom_${r.id}`,
      id: r.id,
      name: r.name,
      values: (r.values || []).map(v => ({ ...v, label: v.label || v.value })),
      isSystem: false,
    })),
  ]

  const totalValues = allRefs.reduce((sum, r) => sum + r.values.length, 0)

  if (loading) return <div className="admin-page">Загрузка...</div>

  return (
    <div className="admin-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          {canManage && (
            <button
              onClick={() => setModal({ type: 'create_reference' })}
              className="btn-primary"
              style={{ marginRight: '8px' }}
            >
              + Создать справочник
            </button>
          )}
        </div>
        <button
          onClick={handleSync}
          className="btn-primary"
          disabled={syncing}
          style={{ minWidth: '200px' }}
        >
          {syncing ? 'Синхронизация...' : 'Синхронизировать из Google Sheets'}
        </button>
      </div>

      <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
        <p style={{ margin: '0' }}>
          <strong>Всего справочников:</strong> {allRefs.length} |
          <strong style={{ marginLeft: '15px' }}>Всего значений:</strong> {totalValues}
        </p>
        <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#666' }}>
          Справочные значения используются для валидации и отображения данных в системе.
        </p>
      </div>

      {allRefs.length === 0 ? (
        <div style={{ padding: '40px', textAlign: 'center', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #ddd' }}>
          <p style={{ fontSize: '18px', color: '#666', marginBottom: '20px' }}>
            Справочники пусты. Создайте справочник или синхронизируйте из Google Sheets.
          </p>
        </div>
      ) : (
        <div className="reference-list">
          {allRefs.map(ref => {
            const isExpanded = expandedKeys[ref.key] !== false
            return (
              <div key={ref.key} className="reference-card" style={{ marginBottom: '20px', border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden' }}>
                <div
                  onClick={() => toggleExpand(ref.key)}
                  style={{
                    padding: '15px 20px',
                    backgroundColor: '#f9f9f9',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: isExpanded ? '1px solid #ddd' : 'none',
                  }}
                >
                  <div>
                    <h3 style={{ margin: 0, fontSize: '18px' }}>{ref.name}</h3>
                    <p style={{ margin: '5px 0 0 0', fontSize: '14px', color: '#666' }}>
                      {ref.values.length} {ref.values.length === 1 ? 'значение' : ref.values.length < 5 ? 'значения' : 'значений'}
                    </p>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {canManage && !ref.isSystem && (
                      <button
                        type="button"
                        className="btn-icon btn-danger"
                        onClick={(e) => { e.stopPropagation(); handleDeleteReference(ref.id) }}
                        title="Удалить справочник"
                      >
                        <Icon name="trash" size={18} />
                      </button>
                    )}
                    <span style={{ fontSize: '20px', color: '#666' }}>{isExpanded ? '▼' : '▶'}</span>
                  </div>
                </div>

                {isExpanded && (
                  <div style={{ padding: '15px 20px', backgroundColor: '#fff' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
                      {ref.values.map(val => (
                        <div
                          key={val.id}
                          style={{
                            padding: '8px 12px',
                            backgroundColor: '#f9f9f9',
                            borderRadius: '4px',
                            border: '1px solid #e0e0e0',
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                          }}
                        >
                          <span>{val.label || val.name || val.value}</span>
                          {canManage && (
                            <>
                              <button
                                type="button"
                                className="btn-icon"
                                onClick={() => setValueEdit({
                                  isSystem: ref.isSystem,
                                  refType: ref.isSystem ? ref.key : null,
                                  refId: ref.isSystem ? null : ref.id,
                                  value: val,
                                })}
                                title="Редактировать"
                              >
                                <Icon name="edit" size={18} />
                              </button>
                              <button
                                type="button"
                                className="btn-icon btn-danger"
                                onClick={() => setValueDelete({
                                  isSystem: ref.isSystem,
                                  refType: ref.isSystem ? ref.key : null,
                                  refId: ref.isSystem ? null : ref.id,
                                  valueId: val.id,
                                  valueLabel: val.label || val.name || val.value,
                                })}
                                title="Удалить"
                              >
                                <Icon name="trash" size={18} />
                              </button>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                    {canManage && (
                      <div style={{ marginTop: '12px' }}>
                        <button
                          type="button"
                          className="btn-secondary"
                          onClick={() => setModal({
                            type: 'add_value',
                            isSystem: ref.isSystem,
                            refType: ref.isSystem ? ref.key : null,
                            refId: ref.isSystem ? null : ref.id,
                          })}
                        >
                          + Добавить значение
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Modal: create reference */}
      {modal?.type === 'create_reference' && (
        <ReferenceModal
          onSave={(name, values) => handleCreateReference(name, values)}
          onClose={() => setModal(null)}
        />
      )}

      {/* Modal: add value */}
      {modal?.type === 'add_value' && (
        <AddValueModal
          isSystem={modal.isSystem}
          refType={modal.refType}
          refId={modal.refId}
          onSave={(a, b, c) => {
            if (modal.isSystem) handleAddSystemValue(modal.refType, a, b)
            else handleAddCustomValue(modal.refId, a, b)
          }}
          onClose={() => setModal(null)}
        />
      )}

      {/* Modal: edit value */}
      {valueEdit && (
        <EditValueModal
          value={valueEdit.value}
          isSystem={valueEdit.isSystem}
          onSave={(name, code, confirm) => {
            if (valueEdit.isSystem) handleUpdateSystemValue(valueEdit.refType, valueEdit.value.id, name, code, confirm)
            else handleUpdateCustomValue(valueEdit.refId, valueEdit.value.id, name, code, confirm)
          }}
          onClose={() => setValueEdit(null)}
        />
      )}

      {/* Modal: delete value */}
      {valueDelete && (
        <div className="modal-overlay" onClick={() => setValueDelete(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '450px' }}>
            {valueDelete.showReplaceModal ? (
              <>
                <h3>Значение используется</h3>
                <p>«{valueDelete.valueLabel}» используется в {valueDelete.usageCount} сущностях.</p>
                <p>Выберите значение для замены или удалите с потерей данных.</p>
                <div className="form-group">
                  <label>Заменить на</label>
                  <select value={replaceWithId} onChange={e => setReplaceWithId(e.target.value)}>
                    <option value="">— Выберите —</option>
                    {(() => {
                      const ref = allRefs.find(r =>
                        (valueDelete.isSystem && r.key === valueDelete.refType) ||
                        (!valueDelete.isSystem && r.id === valueDelete.refId)
                      )
                      return (ref?.values || [])
                        .filter(v => v.id !== valueDelete.valueId)
                        .map(v => (
                          <option key={v.id} value={v.id}>{v.label || v.name || v.value}</option>
                        ))
                    })()}
                  </select>
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn-secondary" onClick={() => setValueDelete(null)}>Отмена</button>
                  <button type="button" className="btn-secondary" onClick={confirmDeleteWithReplace}>Заменить</button>
                  <button type="button" className="btn-danger" onClick={confirmDeleteWithDataLoss}>Удалить с потерей данных</button>
                </div>
              </>
            ) : (
              <>
                <h3>Удалить значение</h3>
                <p>Удалить «{valueDelete.valueLabel}»?</p>
                <div className="modal-actions">
                  <button type="button" className="btn-secondary" onClick={() => setValueDelete(null)}>Отмена</button>
                  <button type="button" className="btn-danger" onClick={() => handleDeleteValue(valueDelete, null, false)}>Удалить</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function ReferenceModal({ onSave, onClose }) {
  const [name, setName] = useState('')
  const [values, setValues] = useState([{ value: '', label: '' }])

  const addRow = () => setValues(v => [...v, { value: '', label: '' }])
  const removeRow = (i) => setValues(v => v.filter((_, j) => j !== i))
  const updateRow = (i, key, val) => setValues(v => v.map((row, j) => j === i ? { ...row, [key]: val } : row))

  const handleSubmit = (e) => {
    e.preventDefault()
    const vs = values.filter(r => (r.value || '').trim())
    onSave(name.trim(), vs)
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-content--ds" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '520px', maxHeight: '90vh', overflow: 'auto' }}>
        <h3>Создать справочник</h3>
        <form onSubmit={handleSubmit} className="modal-body-ds">
          <FormGroup label="Название">
            <Input type="text" value={name} onChange={(e) => setName(e.target.value)} required placeholder="например Источники" />
          </FormGroup>
          <div className="ui-form-group" style={{ overflow: 'hidden' }}>
            <span className="ui-form-group-label">Значения</span>
            {values.map((row, i) => (
              <div key={i} style={{ display: 'flex', gap: 'var(--spacing-sm)', marginBottom: 'var(--spacing-sm)', alignItems: 'center', minWidth: 0 }}>
                <Input
                  type="text"
                  placeholder="Значение"
                  value={row.value}
                  onChange={(e) => updateRow(i, 'value', e.target.value)}
                  style={{ flex: '1 1 0', minWidth: 0 }}
                />
                <Input
                  type="text"
                  placeholder="Подпись"
                  value={row.label}
                  onChange={(e) => updateRow(i, 'label', e.target.value)}
                  style={{ flex: '1 1 0', minWidth: 0 }}
                />
                <button type="button" className="btn-icon btn-danger" onClick={() => removeRow(i)} style={{ flexShrink: 0 }} title="Удалить">
                  <Icon name="trash" size={18} />
                </button>
              </div>
            ))}
            <Button type="button" variant="secondary" onClick={addRow}>+ Добавить</Button>
          </div>
          <div className="modal-actions">
            <Button type="button" variant="secondary" onClick={onClose}>Отмена</Button>
            <Button type="submit" variant="primary">Создать</Button>
          </div>
        </form>
      </div>
    </div>
  )
}

function AddValueModal({ isSystem, refType, refId, onSave, onClose }) {
  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const [value, setValue] = useState('')
  const [label, setLabel] = useState('')

  const handleSubmit = (e) => {
    e.preventDefault()
    if (isSystem) onSave(name.trim(), (code || name).trim())
    else onSave((value || label).trim(), (label || value).trim())
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px' }}>
        <h3>Добавить значение</h3>
        <form onSubmit={handleSubmit}>
          {isSystem ? (
            <>
              <div className="form-group">
                <label>Название</label>
                <input type="text" value={name} onChange={e => setName(e.target.value)} required />
              </div>
              <div className="form-group">
                <label>Код (необязательно)</label>
                <input type="text" value={code} onChange={e => setCode(e.target.value)} />
              </div>
            </>
          ) : (
            <>
              <div className="form-group">
                <label>Значение</label>
                <input type="text" value={value} onChange={e => setValue(e.target.value)} required />
              </div>
              <div className="form-group">
                <label>Подпись (необязательно)</label>
                <input type="text" value={label} onChange={e => setLabel(e.target.value)} />
              </div>
            </>
          )}
          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Отмена</button>
            <button type="submit" className="btn-primary">Добавить</button>
          </div>
        </form>
      </div>
    </div>
  )
}

function EditValueModal({ value, isSystem, onSave, onClose }) {
  const [name, setName] = useState(value?.name || value?.value || '')
  const [code, setCode] = useState(value?.code || '')
  const [label, setLabel] = useState(value?.label || '')

  const handleSubmit = (e) => {
    e.preventDefault()
    if (isSystem) {
      onSave(name, code || name, false)
    } else {
      onSave(name || label, label, false)
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px' }}>
        <h3>Редактировать значение</h3>
        <form onSubmit={handleSubmit}>
          {isSystem ? (
            <>
              <div className="form-group">
                <label>Название</label>
                <input type="text" value={name} onChange={e => setName(e.target.value)} required />
              </div>
              <div className="form-group">
                <label>Код (необязательно)</label>
                <input type="text" value={code} onChange={e => setCode(e.target.value)} />
              </div>
            </>
          ) : (
            <>
              <div className="form-group">
                <label>Значение</label>
                <input type="text" value={name} onChange={e => setName(e.target.value)} required />
              </div>
              <div className="form-group">
                <label>Подпись (необязательно)</label>
                <input type="text" value={label} onChange={e => setLabel(e.target.value)} />
              </div>
            </>
          )}
          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Отмена</button>
            <button type="submit" className="btn-primary">Сохранить</button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AdminReference
